import _extends from "@babel/runtime/helpers/extends";
import _defineProperty from "@babel/runtime/helpers/defineProperty";
import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
import _objectWithoutProperties from "@babel/runtime/helpers/objectWithoutProperties";
var _excluded = ["children", "onResizeEnd", "resizable", "className", "tableHeight", "align", "defaultHeadCells", "headCells", "index", "horizontalPadding", "width", "headId", "hasCheckboxColumn"];
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
/* © 2021 Promethean. All Rights Reserved.
 *
 * Unauthorized copying of this file or any part of this file
 * via any medium is strictly prohibited.
 */
import React, { useRef, useEffect, useState } from 'react';
import classname from 'classnames';
import TableCell from '@mui/material/TableCell';
import { ThemeProvider } from '@mui/material/styles';
import { getTableCellTheme } from './resizableTableCellStyles';
import { getCellWidth, getMinCellWidth, DEFAULT_WIDTH, TABLE_HEAD_FIXED_CLASS } from '../../../utils/resizeCellUtils';
import { useWindowSize } from '../../../../utils/resizeHook';
export function ResizableTableCell(props) {
  var children = props.children,
    onResizeEnd = props.onResizeEnd,
    _props$resizable = props.resizable,
    resizable = _props$resizable === void 0 ? true : _props$resizable,
    className = props.className,
    tableHeight = props.tableHeight,
    _props$align = props.align,
    align = _props$align === void 0 ? 'left' : _props$align,
    _props$defaultHeadCel = props.defaultHeadCells,
    defaultHeadCells = _props$defaultHeadCel === void 0 ? [] : _props$defaultHeadCel,
    _props$headCells = props.headCells,
    headCells = _props$headCells === void 0 ? [] : _props$headCells,
    _props$index = props.index,
    index = _props$index === void 0 ? -1 : _props$index,
    _props$horizontalPadd = props.horizontalPadding,
    horizontalPadding = _props$horizontalPadd === void 0 ? 16 : _props$horizontalPadd,
    _props$width = props.width,
    width = _props$width === void 0 ? DEFAULT_WIDTH.minWidth : _props$width,
    headId = props.headId,
    hasCheckboxColumn = props.hasCheckboxColumn,
    otherProps = _objectWithoutProperties(props, _excluded);
  var startX = 0,
    startStyleWidth = 0,
    offset = 0,
    expandWidth = 0;
  var CellRef = useRef(null);
  var headCellsRef = useRef(headCells);
  var _useState = useState(0),
    _useState2 = _slicedToArray(_useState, 2),
    containerHeight = _useState2[0],
    setContainerHeight = _useState2[1];
  var _useState3 = useState(false),
    _useState4 = _slicedToArray(_useState3, 2),
    resizingActive = _useState4[0],
    setResizingActive = _useState4[1];
  var _useWindowSize = useWindowSize(),
    _useWindowSize2 = _slicedToArray(_useWindowSize, 1),
    windowWidth = _useWindowSize2[0];
  var isResizingFixedHeader = function isResizingFixedHeader(elem) {
    var _elem$parentElement;
    return (_elem$parentElement = elem.parentElement) === null || _elem$parentElement === void 0 || (_elem$parentElement = _elem$parentElement.parentElement) === null || _elem$parentElement === void 0 ? void 0 : _elem$parentElement.classList.contains(TABLE_HEAD_FIXED_CLASS);
  };
  var isResizingFixedHeadCell = CellRef.current && isResizingFixedHeader(CellRef.current);
  useEffect(function () {
    tableHeight && setContainerHeight(tableHeight);
  }, [tableHeight]);
  var onResizeStart = function onResizeStart(resizingIndex) {
    var cellsAfterResizingCol = headCells.filter(function (v, i) {
      return i > resizingIndex;
    });
    var restWidth = cellsAfterResizingCol.reduce(function (a, b) {
      return a + (b.idleWidth || 0);
    }, 0);

    // the re-assignable space: the sum of the idle width of the right columns
    expandWidth = restWidth;
  };
  var onMouseDown = function onMouseDown(e) {
    startX = e.clientX;
    //@ts-ignore
    var cell = e.target.parentElement;
    var styleWidth = cell.style.width;
    startStyleWidth = Number(styleWidth.substring(0, styleWidth.length - 2)) || cell.clientWidth - horizontalPadding;
    setResizingActive(true);
    if (resizable && index >= 0 && expandWidth === 0) {
      onResizeStart(index);
    }
    document.addEventListener('mousemove', mouseMoveHandler);
    document.addEventListener('mouseup', mouseUpHandler);
  };

  // when resizing the fixed head, should sync with the original head to ensure the width is the same
  var syncFixedHeadCellWidth = function syncFixedHeadCellWidth(index, width) {
    try {
      if (isResizingFixedHeadCell) {
        var originalHeadElem = document.querySelector("#".concat(headId, " > tr"));
        if (originalHeadElem) {
          var resizingCell = originalHeadElem.childNodes[hasCheckboxColumn ? index + 1 : index];
          if (resizingCell) {
            resizingCell.style.width = "".concat(width, "px");
          }
        }
      }
    } catch (e) {
      throw new Error('no fixed head element found');
    }
  };
  var onResizeCell = function onResizeCell(offset, resizingIndex) {
    var _offset = offset;
    var currentTempHeadCells = headCells;
    var rightIdleCols = currentTempHeadCells.filter(function (v, i) {
      return i > resizingIndex && (v.idleWidth || 0) > 0;
    });
    var idleWidths = rightIdleCols.reduce(function (a, b) {
      return a + (b.idleWidth || 0);
    }, 0);
    if (_offset > idleWidths) {
      _offset = idleWidths;
    }
    var calcShrinkWidth = function calcShrinkWidth(v, i) {
      var defaultIdleWidth = v.idleWidth || 0;
      // resize the last column
      if (resizingIndex === currentTempHeadCells.length - 1) {
        // resize to right, don't allow resize
        if (offset >= 0) {
          return {
            width: getCellWidth(v),
            idleWidth: defaultIdleWidth
          };
        } else {
          // resize to left, the columns < the last column: the offset will be distributed equally to columns smaller than the resizing index
          var widthB = getCellWidth(v);
          var minWidth = getMinCellWidth(defaultHeadCells, v);
          var shrinkWidth = _offset;
          if (i < resizingIndex) {
            var leftCols = currentTempHeadCells.filter(function (v, i) {
              return i < resizingIndex;
            });
            shrinkWidth = -_offset / leftCols.length;
          }
          return {
            width: widthB + shrinkWidth,
            idleWidth: widthB + shrinkWidth - minWidth <= 0 ? 0 : widthB + shrinkWidth - minWidth
          };
        }
        // resize the NonLastColumn
      } else {
        if (i < resizingIndex) {
          // the index smaller than resizing column shouldn't change any width
          return {
            width: getCellWidth(v),
            idleWidth: defaultIdleWidth
          };
        } else if (i === resizingIndex) {
          // the current resizing column
          var newCellWidth = getCellWidth(v) + _offset;
          var newIdleWidth = newCellWidth - getMinCellWidth(defaultHeadCells, v);
          return {
            width: newCellWidth,
            idleWidth: newIdleWidth <= 0 ? 0 : newIdleWidth
          };
        } else {
          // resizing column is expand
          if (_offset > 0) {
            if (defaultIdleWidth > 0) {
              // has idle columns shrink as the rate
              var assignableOffset = _offset * (defaultIdleWidth / idleWidths);
              return {
                width: getCellWidth(v) - assignableOffset,
                idleWidth: defaultIdleWidth - assignableOffset
              };
            } else {
              // no idle column doesn't change
              return {
                width: getCellWidth(v),
                idleWidth: defaultIdleWidth
              };
            }
          } else {
            // resizing column is shrink, the right columns expand equally
            var rightCols = currentTempHeadCells.filter(function (v, i) {
              return i > resizingIndex;
            });
            var averageExpandWidth = _offset / rightCols.length;
            var _newCellWidth = getCellWidth(v) - averageExpandWidth;
            var _newIdleWidth = _newCellWidth - getMinCellWidth(defaultHeadCells, v);
            return {
              width: _newCellWidth,
              idleWidth: _newIdleWidth <= 0 ? 0 : _newIdleWidth
            };
          }
        }
      }
    };
    var finalRes = currentTempHeadCells.map(function (v, i) {
      var newWidth = calcShrinkWidth(v, i).width;
      var currentCell = CellRef.current;
      if (currentCell) {
        var _currentCell$parentEl;
        var cell = (_currentCell$parentEl = currentCell.parentElement) === null || _currentCell$parentEl === void 0 ? void 0 : _currentCell$parentEl.childNodes[hasCheckboxColumn ? i + 1 : i];
        if (cell) {
          cell.style.width = "".concat(newWidth, "px");
          syncFixedHeadCellWidth(i, newWidth);
        }
      }
      return _objectSpread(_objectSpread({}, v), {}, {
        width: newWidth,
        idleWidth: calcShrinkWidth(v, i).idleWidth
      });
    });
    headCellsRef.current = finalRes;
  };
  var setCurrentCellWidth = function setCurrentCellWidth(width) {
    if (CellRef.current) {
      CellRef.current.style.width = "".concat(width, "px");
      syncFixedHeadCellWidth(index, width);
    }
  };
  var setCellsWidth = function setCellsWidth(currentCellWidth, offset, resizingIndex) {
    setCurrentCellWidth(currentCellWidth);
    onResizeCell(offset, resizingIndex);
  };

  // initialize the cell width
  useEffect(function () {
    setCurrentCellWidth(width);
  }, [width]);
  useEffect(function () {
    if (resizable) {
      setCurrentCellWidth(width);
    }
  }, [windowWidth, resizable, width]);
  useEffect(function () {
    if (resizable) {
      headCellsRef.current = headCells;
    }
  }, [resizable, headCells]);
  var mouseMoveHandler = function mouseMoveHandler(e) {
    e.preventDefault();
    var currentWidth = startStyleWidth + e.clientX - startX;
    if (e.clientX - startX === 0) return;
    var canBeResized = e.clientX - startX > 0 && e.clientX - startX <= expandWidth ||
    //expand
    e.clientX - startX < 0 && currentWidth >= getMinCellWidth(defaultHeadCells, headCells[index]); //shrink

    if (canBeResized) {
      setResizingActive(true);
      offset = e.clientX - startX;
      setCellsWidth(currentWidth, offset, index);
    } else {
      setResizingActive(false);
    }
  };
  var mouseUpHandler = function mouseUpHandler() {
    setResizingActive(false);
    onResizeEnd === null || onResizeEnd === void 0 || onResizeEnd(headCellsRef.current);
    expandWidth = 0;
    document.removeEventListener('mousemove', mouseMoveHandler);
    document.removeEventListener('mouseup', mouseUpHandler);
  };
  return /*#__PURE__*/React.createElement(ThemeProvider, {
    theme: getTableCellTheme({
      height: containerHeight
    })
  }, /*#__PURE__*/React.createElement(TableCell, _extends({
    ref: CellRef,
    className: classname('EmbersResizableTableCell', className),
    align: align
  }, otherProps), /*#__PURE__*/React.createElement("div", null, children), resizable && /*#__PURE__*/React.createElement("span", {
    onMouseDown: onMouseDown,
    className: classname('resizing-line', {
      active: resizingActive
    })
  })));
}